// Component: AlgoDashboard.jsx
// Purpose: Algo Trading dashboard orchestrator — data fetching, state, layout shell

import React, { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageHeader, Tabs, Segmented, Skeleton } from '../../components/ds';
import { useStateContext } from '../../contexts/ContextProvider';
import { useAuth } from '../../contexts/AuthContext';
import { useTradingData } from '../../contexts/TradingDataContext';
import ModeToggle from './components/ModeToggle';
import SummaryStrip from './components/SummaryStrip';
import { MdGridView, MdViewList } from 'react-icons/md';
import AlgoStrategyCard from './components/AlgoStrategyCard';
import AlgoStrategyListItem from './components/AlgoStrategyListItem';
import TradeHistory from '../../components/trading/TradeHistory';
import PositionsTable from '../../components/trading/PositionsTable';
import { useBreakpoint } from '../../hooks/useBreakpoint';
import { useSnackbar } from '../../contexts/SnackbarContext';
import StrategyService from '../../api/services/strategies';
import MobileAlgoDashboard from '../../components/mobile/MobileAlgoDashboard';
import { isExchangeOpenNow } from '../../utils/exchangeHours';

/*>>> TAB DEFINITIONS <<<*/
const TABS = [
  { key: 'strategies', label: 'Strategies' },
  { key: 'orders', label: 'Orders' },
  { key: 'positions', label: 'Positions' },
];

function AlgoDashboard() {
  const { currentColor } = useStateContext();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { isPhone } = useBreakpoint();
  const { showSnackbar } = useSnackbar();

  // --> UI-only state
  const [mode, setMode] = useState('all');
  const [activeTab, setActiveTab] = useState('strategies');
  const [viewMode, setViewMode] = useState('grid');
  const [includeStopped] = useState(true);

  // --> Centralized trading data from TradingDataProvider
  const { orders, positions, deployments, performance, isInitialLoading, error, days, setDays } = useTradingData();

  //! D10 — this is a strategy-performance dashboard: it must NEVER render broker-truth (BD) rows.
  //  Scope the shared context positions to leg==='ALGO' ONCE, here, then feed algoPositions to every
  //  consumer below (stats lookup, TradeHistory, PositionsTable). The R7.2 Source badge on the
  //  PositionsTable instance below will consistently read "Algo" — the correct, expected rendering.
  const algoPositions = useMemo(
    () => positions.filter(p => (p.leg || 'BD') === 'ALGO'),
    [positions],
  );

  // --> Merge deployments with positions/performance/orders from context
  const strategies = useMemo(() => {
    return deployments.map(dep => {
      const sid = `${user?.id}:${dep.strategy_id}:${dep.version_id}:${dep.resolved_instrument_id || dep.instrument_id}:${dep.timeframe}`;
      const pos = algoPositions.find(p => p.strategy_instance_id === sid);
      const perf = performance.find(p => p.strategy_instance_id === sid);
      return {
        id: dep.deployment_id,
        strategyId: dep.strategy_id,
        name: dep.strategy_name,
        version: dep.version_number,
        mode: dep.mode,
        status: dep.status,
        stop_reason: dep.stop_reason,
        dead_instrument: dep.dead_instrument,
        timeframe: dep.timeframe,
        //! R5.3/P8 (money-path #30) — resolved_* ?? frozen fallback, mirrors the SII
        //  pattern already proven at :57 (resolved_instrument_id || instrument_id).
        //  `instrument` feeds AlgoStrategyListItem's extractSymbol() last-resort fallback.
        instrument: dep.resolved_instrument_id ?? dep.instrument_id,
        symbol: dep.resolved_symbol ?? dep.instrument_symbol ?? '',
        instrumentType: dep.instrument_type || '',
        isContinuous: dep.is_continuous || false,
        rolloverDays: dep.rollover_days || 0,
        startTime: dep.started_at,
        stoppedAt: dep.stopped_at,
        //! The ONE session source for the liveness label: MDMS-fed exchange status, tri-state
        //  (true | false | 'UNKNOWN'); only strict `false` reads as closed. Re-read each time
        //  this memo recomputes (every runtime_health push / deployments bump).
        exchangeOpen: isExchangeOpenNow(dep.instrument_exchange),
        //! Runtime-status-live — the worker's runtime_health blob is the SINGLE source of
        //  runtime facts for a RUNNING deployment (REST seed and WS push store the same raw
        //  blob); stopped rows read the DB-backed top-level columns. `null` is a real,
        //  distinct state ("nobody is counting" / no blob yet), never coerced to a falsy
        //  default here — deriveLivenessNote (utils/liveness.js) is the ONLY place that turns
        //  these into a user-facing label. `runtimeOffline` is set by the per-deployment
        //  heartbeat deadline in TradingDataContext (or a shutdown blob).
        runtimeHealth: dep.runtime_health ?? null,
        runtimeOffline: dep.runtime_offline === true,
        ordersPlaced: dep.orders_placed,
        //! F4 (144-1 QA): total_pnl is NULL when the family's charges cannot be resolved. It is
        //  never coerced to a P&L figure here: `pnl` keeps the card's numeric contract only,
        //  while `pnlUnresolved` is the truth the totals and the notice below read.
        pnl: perf?.total_pnl ?? 0,
        pnlUnresolved: perf != null && perf.total_pnl == null,
        winRate: perf?.win_rate || 0,
        totalTrades: perf?.total_trades || 0,
        position: pos || null,
        openOrdersCount: 0,
        strategyOrders: orders.filter(o => o.strategy_instance_id === sid),
      };
    });
  }, [deployments, algoPositions, performance, orders, user?.id]);

  // --> Filter by mode
  const filteredStrategies = useMemo(() => {
    let filtered = mode === 'all' ? strategies : strategies.filter(s => s.mode === mode);

    //--> Sort: running first (by started_at desc), stopped after (by stopped_at desc)
    return filtered.sort((a, b) => {
      if (a.status === 'running' && b.status !== 'running') return -1;
      if (a.status !== 'running' && b.status === 'running') return 1;
      if (a.status === 'running' && b.status === 'running') {
        return new Date(b.startTime || 0) - new Date(a.startTime || 0);
      }
      return new Date(b.stoppedAt || 0) - new Date(a.stoppedAt || 0);
    });
  }, [mode, strategies]);

  // --> Summary data
  const summaryData = useMemo(() => {
    const running = filteredStrategies.filter(s => s.status === 'running');
    //--> F4: a strategy whose net P&L is unresolved contributes NOTHING to the totals (and is
    //    counted in pnlUnresolvedCount so the dashboard says so) - never a silent Rs 0.
    const resolved = filteredStrategies.filter(s => !s.pnlUnresolved);
    const totalPnl = resolved.reduce((sum, s) => sum + s.pnl, 0);
    const livePnl = resolved.filter(s => s.mode === 'live').reduce((sum, s) => sum + s.pnl, 0);
    const paperPnl = resolved.filter(s => s.mode === 'paper').reduce((sum, s) => sum + s.pnl, 0);
    const pnlUnresolvedCount = filteredStrategies.length - resolved.length;
    const liveCount = filteredStrategies.filter(s => s.mode === 'live').length;
    const paperCount = filteredStrategies.filter(s => s.mode === 'paper').length;
    const totalTrades = filteredStrategies.reduce((sum, s) => sum + s.totalTrades, 0);
    const winningTrades = filteredStrategies.reduce(
      (sum, s) => sum + Math.round(s.totalTrades * s.winRate), 0
    );

    return {
      totalPnl,
      livePnl,
      paperPnl,
      pnlUnresolvedCount,
      activeStrategies: running.length,
      liveCount,
      paperCount,
      openPositions: filteredStrategies.filter(s => s.position).length,
      winRate: totalTrades > 0 ? (winningTrades / totalTrades) * 100 : 0,
      totalTrades,
    };
  }, [filteredStrategies]);

  // --> Tab counts
  //! MF-3 (D10) — the Positions tab renders algoPositions (ALGO-leg subset), so its badge count MUST
  //  be scoped to algoPositions too. Using the raw all-legs `positions.length` here would show a count
  //  that doesn't match the rendered rows once any BD/MANUAL position coexists with an ALGO one.
  const tabCounts = useMemo(() => ({
    strategies: filteredStrategies.length,
    orders: orders.length,
    positions: algoPositions.length,
  }), [filteredStrategies, orders, algoPositions]);


  // --> Navigate to strategy detail
  const handleStrategyClick = useCallback(
    (strategyId) => {
      navigate(`/AlgoDashboard/${strategyId}`);
    },
    [navigate],
  );

  // --> Stop a running algo (real endpoint; mirrors StrategyBuilder's flow).
  //     Blocks if an open position exists, then stops the deployment. The
  //     deployments WS pushes running→stopped, so the list updates itself.
  const handleStopStrategy = useCallback(
    async (s) => {
      if (!s?.strategyId || !s?.id) return;
      try {
        if (s.position && s.position.side && s.position.side !== 'FLAT' && s.position.status !== 'CLOSED') {
          showSnackbar('Close the open position before stopping this algo.', 'warning');
          return;
        }
        await StrategyService.stopDeployment(s.strategyId, s.id);
        showSnackbar('Algo stopped.', 'success');
      } catch (err) {
        showSnackbar(err.response?.data?.detail || err.message || 'Stop failed', 'error');
      }
    },
    [showSnackbar],
  );

  // --> Phone tier: same live data (props), phone-shaped presentation.
  if (isPhone) {
    return (
      <MobileAlgoDashboard
        strategies={filteredStrategies}
        summary={summaryData}
        mode={mode}
        onMode={setMode}
        loading={isInitialLoading}
        error={error}
        onOpen={handleStrategyClick}
        onStop={handleStopStrategy}
      />
    );
  }

  return (
    <div className="flex flex-col bg-main-bg dark:bg-main-dark-bg font-display text-fg-2 w-full min-h-screen relative pt-0 px-4 md:px-6 pb-10">
      {/* Standardized page header (common across all pages) */}
      <PageHeader
        title="Algo Trading"
        subtitle="Monitor your deployed strategies and live algo executions."
        actions={
          <div className="flex items-center gap-2">
            <Segmented
              aria-label="Stopped-strategy lookback"
              value={days}
              onChange={setDays}
              options={[
                { value: 1, label: '1D' },
                { value: 7, label: '1W' },
                { value: 30, label: '1M' },
              ]}
            />
            <Segmented
              aria-label="View mode"
              value={viewMode}
              onChange={setViewMode}
              options={[
                { value: 'grid', label: 'Grid', icon: <MdGridView size={14} /> },
                { value: 'list', label: 'List', icon: <MdViewList size={14} /> },
              ]}
            />
            <ModeToggle mode={mode} onChange={setMode} />
          </div>
        }
      />

      {/* --> Tab bar (DS underline tabs) */}
      <Tabs
        className="mb-6"
        value={activeTab}
        onChange={setActiveTab}
        tabs={TABS.map((tab) => ({
          label: tab.label,
          value: tab.key,
          badge: tabCounts[tab.key],
        }))}
      />

      {/* --> Summary strip */}
      <div className="mb-6">
        <SummaryStrip data={summaryData} />
        {summaryData.pnlUnresolvedCount > 0 && (
          <p className="mt-2 text-xs text-warning" data-testid="pnl-unresolved-note">
            Net P&amp;L is unresolved (charges unresolved) for {summaryData.pnlUnresolvedCount}{' '}
            {summaryData.pnlUnresolvedCount === 1 ? 'strategy' : 'strategies'} - the totals above exclude
            {summaryData.pnlUnresolvedCount === 1 ? ' it' : ' them'}.
          </p>
        )}
      </div>

      {/* --> Tab content */}
      <div>
        {/*>>> LOADING STATE <<<*/}
        {isInitialLoading && (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="rounded-xl border border-[var(--divider)] bg-bg-2 p-4 flex flex-col gap-3">
                <div className="flex items-center gap-3">
                  <Skeleton circle w={36} h={36} />
                  <div className="flex-1 flex flex-col gap-2">
                    <Skeleton w="55%" h={12} />
                    <Skeleton w="35%" h={9} />
                  </div>
                  <Skeleton w={52} h={20} radius={6} />
                </div>
                <Skeleton h={40} radius={8} />
                <div className="flex gap-2">
                  <Skeleton w="32%" h={9} />
                  <Skeleton w="32%" h={9} />
                  <Skeleton w="32%" h={9} />
                </div>
              </div>
            ))}
          </div>
        )}

        {/*>>> ERROR STATE <<<*/}
        {!isInitialLoading && error && (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <p className="text-sm text-danger mb-2">Failed to load dashboard</p>
            <p className="text-xs text-fg-2">{error}</p>
          </div>
        )}

        {/*>>> STRATEGIES TAB <<<*/}
        {!isInitialLoading && !error && activeTab === 'strategies' && (
          <>
            {filteredStrategies.length > 0 ? (
              viewMode === 'grid' ? (
                //--> Grid view
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4">
                  {filteredStrategies.map((strategy) => (
                    <AlgoStrategyCard
                      key={strategy.id}
                      strategy={strategy}
                      onClick={handleStrategyClick}
                      onStop={handleStopStrategy}
                    />
                  ))}
                </div>
              ) : (
                //--> List view
                <div className="flex flex-col gap-2">
                  {filteredStrategies.map((strategy) => (
                    <AlgoStrategyListItem
                      key={strategy.id}
                      strategy={strategy}
                      onClick={handleStrategyClick}
                    />
                  ))}
                </div>
              )
            ) : (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <div className="p-4 rounded-2xl bg-bg-3 mb-4">
                  <svg className="w-8 h-8 text-fg-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <p className="text-sm font-medium text-fg-2 mb-1">
                  {`No ${mode === 'all' ? '' : mode + ' '}strategies found`}
                </p>
                <p className="text-xs text-fg-3">
                  Deploy a strategy from the Strategy Builder to get started
                </p>
              </div>
            )}
          </>
        )}

        {/*>>> ORDERS TAB <<<*/}
        {!isInitialLoading && !error && activeTab === 'orders' && (
          <TradeHistory
            orders={orders}
            positions={algoPositions}
            mode="full"
          />
        )}

        {/*>>> POSITIONS TAB <<<*/}
        {!isInitialLoading && !error && activeTab === 'positions' && <PositionsTable positions={algoPositions} showStrategy={true} />}
      </div>
    </div>
  );
}

export default AlgoDashboard;
